import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { leadsAPI, sanitizeAxiosErrorForCapture } from '../lib/api';
import { Sentry } from '../lib/sentry';
import type { SetMarketingConsentBody } from '../types/consent';
import type { LeadDetail } from '../types/leads';
import { invalidateLeadDetail } from './useLeads';

const ACTION = 'lead.consent_updated';

export function httpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | null)?.response?.status;
}

function isRequestError(error: unknown): error is AxiosError {
  return (error as { isAxiosError?: unknown } | null)?.isAxiosError === true;
}

interface SetMarketingConsentVariables extends SetMarketingConsentBody {
  leadId: string;
}

interface SetMarketingConsentResult {
  id: string;
  marketingOptIn: boolean | null;
}

export function useSetMarketingConsent(): UseMutationResult<
  SetMarketingConsentResult,
  Error,
  SetMarketingConsentVariables
> {
  const queryClient = useQueryClient();
  const { backendUser, currentOrganization } = useAuth();

  return useMutation<SetMarketingConsentResult, Error, SetMarketingConsentVariables>({
    mutationFn: async ({ leadId, ...body }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');

      const baseFields = {
        action: ACTION,
        organizationId: orgId,
        userId: backendUser?.id,
        leadId,
        granted: body.granted,
        source: body.source,
      };

      Sentry.addBreadcrumb({
        category: 'lead',
        message: 'marketing_consent_changed',
        level: 'info',
        data: baseFields,
      });

      try {
        const { data } = await leadsAPI.setMarketingConsent(leadId, orgId, body);
        Sentry.metrics?.increment?.(ACTION, 1, { tags: { outcome: 'success' } });
        return data;
      } catch (err) {
        Sentry.addBreadcrumb({
          category: 'lead',
          message: 'marketing_consent_changed.failed',
          level: 'error',
          data: { ...baseFields, error: (err as Error).message },
        });
        Sentry.metrics?.increment?.(ACTION, 1, { tags: { outcome: 'error' } });
        if (httpStatus(err) !== 403) {
          Sentry.captureException(isRequestError(err) ? sanitizeAxiosErrorForCapture(err) : err);
        }
        throw err;
      }
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<LeadDetail>({ queryKey: ['lead', variables.leadId] }, (old) =>
        old ? { ...old, marketingOptIn: data.marketingOptIn } : old,
      );
      invalidateLeadDetail(queryClient, variables.leadId);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}
